import { useClientPortalAPI } from "@certn/api";
import {
  Container,
  Divider,
  GhostIconButton,
  Loader,
  NeutralTag,
  SectionMessage,
  SuccessTag,
  TextField,
} from "@certn/cascadia-react";
import { Stack, Text } from "@certn/cascadia";
import { NavigationBar } from "@certn/cascadia-react/components/navigation/navigation-top/NavigationBar";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { CustomTemplateQuestionPreview } from "../templates/CustomTemplateQuestionPreview";

const navStyles = {
  bar: css({
    display: "flex",
    justifyContent: "flex-start",
  }),
};

const contentTop = css({ paddingTop: "var(--space-6)" });

export function FitnessProprietyTemplateView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useClientPortalAPI(
    id ? `/api/internal/client-portal/fitness-propriety-templates/${id}/` : null
  );

  if (isLoading || (id && !data && !error)) {
    return <Loader />;
  }

  if (error || !data || !id) {
    return (
      <SectionMessage type="danger" sectionTitle={t("Unable to load template")}>
        {error ? String(error.message) : t("This template could not be found.")}
      </SectionMessage>
    );
  }

  const orderedQuestions = [...(data.questions ?? [])].sort(
    (a, b) => a.display_priority - b.display_priority
  );

  // Bucket conditional questions under their parent so we can nest them in
  // the parent's collapsible body. Every F&P parent has a `controls_display_of`
  // array listing the IDs of follow-up questions it triggers (the standard
  // "If yes, please add comments" prompt), so we use that to look the
  // follow-ups up directly rather than reverse-matching display_conditions.
  const questionsById = new Map(orderedQuestions.map((q) => [q.id, q]));
  const followUpsByParentId = new Map(
    orderedQuestions
      .filter((q) => q.display_conditions.length === 0)
      .map((parent) => [
        parent.id,
        parent.controls_display_of
          .map((controls) => questionsById.get(controls.dependant_question.id))
          .filter(<T,>(q: T | undefined): q is T => q !== undefined),
      ])
  );
  const topLevelQuestions = orderedQuestions.filter(
    (q) => q.display_conditions.length === 0
  );

  return (
    <Container layout="applicant/narrow" className={contentTop}>
      <NavigationBar testID="fitness-propriety-template-nav">
        <div className={navStyles.bar}>
          <GhostIconButton
            testID="fitness-propriety-template-close"
            icon="close"
            size="medium"
            onPress={() => navigate("/settings/fitness-propriety-templates")}
            aria-label={t("Back to fitness and propriety templates")}
          />
        </div>
      </NavigationBar>
      <Stack gap="space-8">
        <Stack gap="space-3">
          <Text as="h1" style="global/title-small">
            {t("Fitness & Propriety template")}
          </Text>
          <Text style="form/body-secondary" color="text-weak">
            {t("This template is managed by Certn. It cannot be edited.")}
          </Text>
          <Stack orientation="horizontal" gap="space-3">
            {data.is_active ? (
              <SuccessTag testID="fitness-propriety-active" type="subtle">
                {t("Active")}
              </SuccessTag>
            ) : (
              <NeutralTag testID="fitness-propriety-inactive" type="subtle">
                {t("Inactive")}
              </NeutralTag>
            )}
            <Text style="form/body-secondary" color="text-weak">
              {data.usage_count ?? 0} {t("cases")}
            </Text>
          </Stack>
        </Stack>
        <Stack gap="space-4">
          <TextField
            testID="fitness-propriety-template-name"
            label={t("Template name")}
            value={data.name}
            isDisabled
            isRequired
          />
          <Divider />
          <Stack gap="space-4">
            <Text style="global/heading-large">
              {`${t("Questions")} (${topLevelQuestions.length})`}
            </Text>
            {topLevelQuestions.map((question, index) => (
              <CustomTemplateQuestionPreview
                key={question.id}
                question={question}
                displayNumber={index + 1}
                followUps={followUpsByParentId.get(question.id) ?? []}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
}
